import { useEffect, useState, useRef, Fragment } from "react";
import { createPortal } from "react-dom";
import { BsFillCircleFill } from "react-icons/bs";
import { USER_ROLE } from "../../../Constants/constant";
import CircularLoader from "../CircularLoader/CircularLoader";
import CustomButton from "../Button/CustomButton";
import { roleAccess } from "../../../helpers/RolesPermission";
import { PERMISSION_KEYS, PERMISSION_KEYS_CHILDREN } from "store/constants";
import AddGoal from "./Modal/AddGoal";
import {HiPlus, HiOutlinePencil, HiOutlineTrash, HiOutlineExternalLink} from "react-icons/hi";
import {apiDeleteRequest, apiGetRequest} from "../../../helpers/Requests";
import {baseURLImage} from "../../../Utils/utils";
import {toast} from "react-toastify";
import { useParams } from "react-router-dom";
import {Dialog, Transition} from "@headlessui/react";
import {ExclamationIcon} from "@heroicons/react/outline";
import LoaderButton from "../LoaderButton/LoaderButton";
import { FiInfo } from "react-icons/fi";
import useCareLabels from "hooks/useCareLabels";


const LikesDislikes = (props) => {
  const {
    isLoading,
    supportPlanData,
    setToggleState,
    viewInitialAssesmentForm,
    addInitialAssesmentForm,
    participantsInfo,
    initialAssessmentEditEnabled = true,
    initialAssessmentAddEnabled = true,
  } = props;
  const { participantLabel } = useCareLabels();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [goals, setGoals] = useState([]);
  const [selectedGoal, setSelectedGoal] = useState(null);
  const [goalIdToDelete, setGoalIdToDelete] = useState(null);
  const [dropdownOpenGoalId, setDropdownOpenGoalId] = useState(null);
  const [dropdownPositions, setDropdownPositions] = useState({});
  const dropdownRefs = useRef({});
  const buttonRefs = useRef({});
  const { uuid } = useParams();

  const getLikes = () => {
    let likes = [];
    let dislikes = [];
    let goals = [];
    if (supportPlanData) {
      const getData = supportPlanData.support_plan_data.find(
        (x) => x.lable === "Interests"
      );
      if (getData && getData.rows && getData.rows.length > 0) {
        getData.rows.forEach((x) => {
          if (x && x.length > 0) {
            x.forEach((y) => {
              if (y.lable === "Likes" && y.value) likes.push(y);
              if (y.lable === "Dislikes" && y.value) dislikes.push(y);
              if (y.lable === "Goals" && y.value) goals.push(y);
            });
          }
        });
      }
    }
    return { likes, dislikes, goals };
  };

  const handleDeleteGoal = async () => {
    if (!goalIdToDelete) return;

    setLoading(true);
    try {
      const response = await apiDeleteRequest(`/sp/participant-goal/${goalIdToDelete}`);

      if (response.status === 200) {
        toast.success("Goal deleted successfully!");
        setGoals(prev => prev.filter(goal => goal.uuid !== goalIdToDelete));
      } else {
        toast.error("Failed to delete goal");
      }
    } catch (error) {
      toast.error(error?.message ?? "Something went wrong");
    } finally {
      setLoading(false);
      setGoalIdToDelete(null);
      setIsDialogOpen(false);
    }
  };

  useEffect(() => {
    const fetchGoals = async () => {
      try {
        const isAdminOrManager = roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]);
        const endPoint = isAdminOrManager ? `/sp/participant-goal?pt_id=${uuid}` : `/worker/participant-goal?pt_id=${uuid}`;
          const response = await apiGetRequest(endPoint);
          if (response?.status === 200) {
            setGoals(response?.data?.data || []);
          }
      } catch (error) {
        toast.error(error?.message ?? "Error fetching goals");
      }
    };

    if (uuid) {
      fetchGoals();
    }
  }, [uuid]);

  const handleGoalAdded = (newGoal) => {
    setGoals(prev => [newGoal, ...prev]);
  };

  // Function to update dropdown position based on button position
  const updatePosition = (goalId) => {
    if (buttonRefs.current[goalId]) {
      const rect = buttonRefs.current[goalId].getBoundingClientRect();
      setDropdownPositions(prev => ({
        ...prev,
        [goalId]: {
          top: rect.bottom + 4, // 4px gap (mt-1 equivalent)
          left: rect.right - 128, // Align right edge (w-32 = 128px)
        }
      }));
    }
  };

  // Update position when dropdown opens
  useEffect(() => {
    if (dropdownOpenGoalId) {
      updatePosition(dropdownOpenGoalId);
    }
  }, [dropdownOpenGoalId]);

  // Handle scroll and resize events to update dropdown position
  useEffect(() => {
    if (dropdownOpenGoalId) {
      const handleScroll = () => {
        updatePosition(dropdownOpenGoalId);
      };
      const handleResize = () => {
        updatePosition(dropdownOpenGoalId);
      };
      
      // Listen to scroll events on all scrollable containers
      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);
      
      return () => {
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [dropdownOpenGoalId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownOpenGoalId) {
        const buttonElement = buttonRefs.current[dropdownOpenGoalId];
        const isClickOnButton = buttonElement && buttonElement.contains(event.target);
        const isClickOnDropdown = event.target.closest('.goal-dropdown-portal');
        
        if (!isClickOnButton && !isClickOnDropdown) {
          setDropdownOpenGoalId(null);
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpenGoalId]);

  const closeDialog = () => {
    setIsDialogOpen(false);
    setGoalIdToDelete(null);
  };

  const handleAddLikes = () => {
    const canOpenAssessment =
      supportPlanData !== null
        ? initialAssessmentEditEnabled
        : initialAssessmentAddEnabled;
    if (
      roleAccess(
        [],
        PERMISSION_KEYS.PARTICIPANT,
        PERMISSION_KEYS_CHILDREN.PARTICIPANT.SHOW
      ) &&
      canOpenAssessment
    ) {
      supportPlanData !== null
        ? viewInitialAssesmentForm(supportPlanData.form_data_uuid)
        : addInitialAssesmentForm();
    }
  };

  const handleAddDislikes = () => {
    const canOpenAssessment =
      supportPlanData !== null
        ? initialAssessmentEditEnabled
        : initialAssessmentAddEnabled;
    if (
      roleAccess(
        [],
        PERMISSION_KEYS.PARTICIPANT,
        PERMISSION_KEYS_CHILDREN.PARTICIPANT.SHOW
      ) &&
      canOpenAssessment
    ) {
      supportPlanData !== null
        ? viewInitialAssesmentForm(supportPlanData.form_data_uuid)
        : addInitialAssesmentForm();
    }
  };

  const likesData = getLikes().likes.filter((x) => x.value && x.value.length > 0);
  const dislikesData = getLikes().dislikes.filter((x) => x.value && x.value.length > 0);
  const canEdit = !roleAccess([USER_ROLE.Sp_Standard_User]) &&
    roleAccess(
      [],
      PERMISSION_KEYS.PARTICIPANT,
      PERMISSION_KEYS_CHILDREN.PARTICIPANT.SHOW
    );

  return (
    <div className='min-h-full'>
      <div className='w-full'>
        <div className='bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden h-full flex flex-col'>
          {/* Compact Header */}
          <div className='bg-light-purple-gradient px-4 sm:px-5 py-3.5 sm:py-4'>
            <div>
              <h2 className='text-lg sm:text-xl font-bold text-white'>
                Goals, Likes & Dislikes
              </h2>
              <p className='text-xs text-white/90 mt-0.5'>
                {participantLabel} preferences and goals
              </p>
            </div>
          </div>

          {isLoading ? (
            <div className='flex justify-center items-center py-12 flex-1'>
              <CircularLoader
                classes='flex justify-center items-center'
                classloader='loader-l'
              />
            </div>
          ) : (
            <div className='p-3 sm:p-4 space-y-4 flex-1 overflow-y-auto custom-scroll'>
              {/* Goals Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Goals
                  </h3>
                  {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) && (
                    <CustomButton
                      clickHandler={() => {
                        setSelectedGoal(null);
                        setIsModalOpen(true);
                      }}
                      label="Add Goal"
                      variant="primary"
                      size="sm"
                      cssClass="!px-3 !py-1.5 text-xs"
                    />
                  )}
                </div>

                <div className="space-y-2 max-h-[200px] overflow-y-auto custom-scroll">
                  {goals.length > 0 ? (
                    goals.map((goal) => (
                      <div
                        key={goal.id}
                        className="flex items-start justify-between p-2.5 bg-gray-50 rounded-lg border border-gray-100 hover:border-gray-200 transition-colors group"
                      >
                        <div className="flex items-start space-x-2.5 flex-1 min-w-0">
                          <BsFillCircleFill className="h-1.5 w-1.5 text-[#4B6CB7] flex-shrink-0 mt-1.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-900 break-words leading-snug">
                              {goal.goal_title}
                            </p>
                          </div>
                        </div>
                        {roleAccess([USER_ROLE.Sp_Admin, USER_ROLE.Sp_manager]) && (
                          <div className="relative flex-shrink-0 ml-2">
                            <button
                              ref={(el) => {
                                buttonRefs.current[goal.uuid] = el;
                              }}
                              className="text-gray-400 hover:text-gray-600 p-1 rounded-md hover:bg-gray-100 transition-colors"
                              onClick={() =>
                                setDropdownOpenGoalId(
                                  dropdownOpenGoalId === goal.uuid ? null : goal.uuid
                                )
                              }
                            >
                              <span className="text-base leading-none">⋮</span>
                            </button>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-xs text-gray-500 bg-gray-50 rounded-lg border border-gray-100">
                      No goals added yet.
                    </div>
                  )}
                </div>
              </div>

              {/* Portal-rendered dropdown menu for goals */}
              {typeof window !== 'undefined' && dropdownOpenGoalId && dropdownPositions[dropdownOpenGoalId] && (() => {
                const currentGoal = goals.find(g => g.uuid === dropdownOpenGoalId);
                const position = dropdownPositions[dropdownOpenGoalId];
                
                if (!currentGoal || !position) return null;
                
                return createPortal(
                  <div 
                    className="goal-dropdown-portal fixed w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-[10000]"
                    style={{
                      top: `${position.top}px`,
                      left: `${position.left}px`,
                    }}
                  >
                    <button
                      className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 rounded-t-lg"
                      onClick={() => {
                        setSelectedGoal(currentGoal);
                        setIsModalOpen(true);
                        setDropdownOpenGoalId(null);
                      }}
                    >
                      <HiOutlinePencil className="h-3.5 w-3.5 text-gray-600" /> Edit
                    </button>
                    <button
                      className="flex items-center gap-2 w-full text-left px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 rounded-b-lg"
                      onClick={() => {
                        setGoalIdToDelete(currentGoal.uuid);
                        setIsDialogOpen(true);
                        setDropdownOpenGoalId(null);
                      }}
                    >
                      <HiOutlineTrash className="h-3.5 w-3.5" /> Delete
                    </button>
                  </div>,
                  document.body
                );
              })()}

              <AddGoal
                open={isModalOpen}
                setOpen={setIsModalOpen}
                handleClose={() => setIsModalOpen(false)}
                participantInfo={{ id: participantsInfo.id }}
                selectedGoal={selectedGoal}
                onGoalAdded={handleGoalAdded}
                onGoalUpdated={(updatedGoal) =>
                  setGoals(prev =>
                    prev.map(goal => (goal.uuid === updatedGoal.uuid ? updatedGoal : goal))
                  )
                }
                onGoalDeleted={(deletedGoalId) =>
                  setGoals(prev => prev.filter(goal => goal.uuid !== deletedGoalId))
                }
              />

              {/* Likes Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className='text-sm font-semibold text-gray-900'>
                      Likes
                    </h3>
                    {canEdit && (
                      <div className="group relative">
                        <FiInfo className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-20">
                          <p>Likes are managed through the Initial Assessment Form. Click "Add/Edit Likes" to update.</p>
                          <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <button
                      onClick={handleAddLikes}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient text-white hover:from-blue-600 hover:to-blue-700 transition-all shadow-sm"
                    >
                      <span>Add/Edit Likes</span>
                      <HiOutlineExternalLink className="h-3 w-3" />
                    </button>
                  )}
                </div>

                <div className="space-y-2 max-h-[120px] overflow-y-auto custom-scroll">
                  {likesData.length > 0 ? (
                    likesData.map((like, index) => (
                      <div
                        key={index}
                        className='flex items-start space-x-2.5 p-2.5 bg-green-50 rounded-lg border border-green-100'
                      >
                        <BsFillCircleFill className='h-1.5 w-1.5 text-green-600 flex-shrink-0 mt-1.5' />
                        <span className='text-xs font-medium text-gray-900 break-words flex-1 leading-snug'>
                          {like.value}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-xs text-gray-500 bg-gray-50 rounded-lg border border-gray-100">
                      {roleAccess([USER_ROLE.Sp_Standard_User]) ? (
                        "No Data found!"
                      ) : (
                        "No likes added yet."
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Dislikes Section */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <h3 className='text-sm font-semibold text-gray-900'>
                      Dislikes
                    </h3>
                    {canEdit && (
                      <div className="group relative">
                        <FiInfo className="h-3.5 w-3.5 text-gray-400 cursor-help" />
                        <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block w-48 p-2 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-20">
                          <p>Dislikes are managed through the Initial Assessment Form. Click "Add/Edit Dislikes" to update.</p>
                          <div className="absolute left-2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                        </div>
                      </div>
                    )}
                  </div>
                  {canEdit && (
                    <button
                      onClick={handleAddDislikes}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient text-white hover:to-blue-700 transition-all shadow-sm"
                    >
                      <span>Add/Edit Dislikes</span>
                      <HiOutlineExternalLink className="h-3 w-3" />
                    </button>
                  )}
                </div>

                <div className="space-y-2 max-h-[120px] overflow-y-auto custom-scroll">
                  {dislikesData.length > 0 ? (
                    dislikesData.map((dislike, index) => (
                      <div
                        key={index}
                        className='flex items-start space-x-2.5 p-2.5 bg-red-50 rounded-lg border border-red-100'
                      >
                        <BsFillCircleFill className='h-1.5 w-1.5 text-red-600 flex-shrink-0 mt-1.5' />
                        <span className='text-xs font-medium text-gray-900 break-words flex-1 leading-snug'>
                          {dislike.value}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-6 text-xs text-gray-500 bg-gray-50 rounded-lg border border-gray-100">
                      {roleAccess([USER_ROLE.Sp_Standard_User]) ? (
                        "No Data found!"
                      ) : (
                        "No dislikes added yet."
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <Transition.Root show={isDialogOpen} as={Fragment}>
        <Dialog as="div"
                onClose={closeDialog}
                className='fixed inset-0 z-50 top-50 overflow-y-auto'
        >
          <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0'
              enterTo='opacity-100'
              leave='ease-in duration-200'
              leaveFrom='opacity-100'
              leaveTo='opacity-0'
          >
            <div className='fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity' />
          </Transition.Child>

          <div className='fixed z-10 inset-0 overflow-y-auto'>
            <div className='flex items-center sm:items-center justify-center min-h-full p-4 text-center sm:p-0'>
              <Transition.Child
                  as={Fragment}
                  enter='ease-out duration-300'
                  enterFrom='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
                  enterTo='opacity-100 translate-y-0 sm:scale-100'
                  leave='ease-in duration-200'
                  leaveFrom='opacity-100 translate-y-0 sm:scale-100'
                  leaveTo='opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95'
              >
                <Dialog.Panel className='relative bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:max-w-lg sm:w-full sm:p-6'>
                  <div className='sm:flex sm:items-start'>
                    <div className='mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10'>
                      <ExclamationIcon
                          className='h-6 w-6 text-red-600'
                          aria-hidden='true'
                      />
                    </div>
                    <div className='mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left'>
                      <Dialog.Title
                          as='h3'
                          className='text-lg leading-6 font-medium text-gray-900'
                      >
                        You're about to remove this goal
                      </Dialog.Title>
                      <div className='mt-2'>
                        <p className='text-sm text-gray-500'>
                          Are you sure you want to delete this goal? This cannot be undone.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className='mt-5 sm:mt-4 sm:flex'>
                    {loading ? (
                        <LoaderButton
                            classes="inline-flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-red-500 border border-red-500 hover:border-red-400 hover:bg-red-400 rounded-md focus:outline-none cursor-block"
                            svgClasses="inline mr-3 w-4 h-4 text-white animate-spin"
                            textbutton="Loading..."
                        />
                    ) : (
                        <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                            onClick={handleDeleteGoal}
                        >
                          Yes, please
                        </button>
                    )}
                    <button
                        type='button'
                        className='ml-2 mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm'
                        onClick={closeDialog}
                    >
                      Ah, nevermind!
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
};

export default LikesDislikes;
